import { z } from 'zod';
import { isoDate } from './fields.ts';

/**
 * A deliberately small recurrence model covering what households use:
 * daily, weekly (optionally on chosen weekdays), monthly by day-of-month and
 * yearly, each with an interval and an optional end (until date or count).
 *
 * Expansion happens in the runtime's local time zone so that "every Monday at
 * 4pm" stays at 4pm across daylight-saving changes. Days that don't exist in a
 * month (the 31st, Feb 29th) are clamped to the month's last day.
 */
export const recurrenceSchema = z.object({
  freq: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
  interval: z.number().int().min(1).max(99),
  /** 0 = Sunday … 6 = Saturday. Weekly only. */
  byWeekday: z.array(z.number().int().min(0).max(6)).max(7).optional(),
  /** Inclusive last date (YYYY-MM-DD). */
  until: isoDate.optional(),
  count: z.number().int().min(1).max(999).optional(),
});
export type Recurrence = z.infer<typeof recurrenceSchema>;

const pad = (n: number) => String(n).padStart(2, '0');

/** Local-midnight Date for a YYYY-MM-DD string. */
export function parseLocalDate(value: string): Date {
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

/** YYYY-MM-DD for a Date, in local time. */
export function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Calendar-day arithmetic that preserves local wall-clock time across DST. */
export function addDaysLocal(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, date.getHours(), date.getMinutes());
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function monthlyOccurrence(start: Date, monthsToAdd: number): Date {
  const target = new Date(start.getFullYear(), start.getMonth() + monthsToAdd, 1);
  const day = Math.min(start.getDate(), daysInMonth(target.getFullYear(), target.getMonth()));
  return new Date(target.getFullYear(), target.getMonth(), day, start.getHours(), start.getMinutes());
}

const DAY_MS = 86_400_000;

function* generate(start: Date, rule: Recurrence, skipPeriods: number): Generator<Date> {
  const interval = rule.interval;
  switch (rule.freq) {
    case 'daily':
      for (let i = skipPeriods; ; i++) yield addDaysLocal(start, i * interval);
    case 'weekly': {
      const weekdays = rule.byWeekday && rule.byWeekday.length > 0 ? [...new Set(rule.byWeekday)].sort((a, b) => a - b) : [start.getDay()];
      const weekAnchor = addDaysLocal(start, -start.getDay());
      for (let w = skipPeriods; ; w++) {
        for (const wd of weekdays) {
          const candidate = addDaysLocal(weekAnchor, w * 7 * interval + wd);
          if (candidate >= start) yield candidate;
        }
      }
    }
    case 'monthly':
      for (let i = skipPeriods; ; i++) yield monthlyOccurrence(start, i * interval);
    case 'yearly':
      for (let i = skipPeriods; ; i++) yield monthlyOccurrence(start, i * interval * 12);
  }
}

/** Whole periods that can be skipped safely before `from` (only when no COUNT). */
function periodsBefore(start: Date, rule: Recurrence, from: Date): number {
  if (rule.count !== undefined || from <= start) return 0;
  const days = Math.floor((from.getTime() - start.getTime()) / DAY_MS);
  const periodDays = { daily: 1, weekly: 7, monthly: 28, yearly: 365 }[rule.freq] * rule.interval;
  // Under-estimate by one period to stay on the safe side of DST/month lengths.
  const approx = Math.floor(days / periodDays) - 1;
  if (rule.freq === 'monthly' || rule.freq === 'yearly') {
    const months = (from.getFullYear() - start.getFullYear()) * 12 + (from.getMonth() - start.getMonth());
    const perPeriod = rule.freq === 'monthly' ? rule.interval : rule.interval * 12;
    return Math.max(0, Math.floor(months / perPeriod) - 1);
  }
  return Math.max(0, approx);
}

export interface ExpandOptions {
  /** Occurrence dates (YYYY-MM-DD) to leave out. */
  exdates?: readonly string[];
  /** Safety cap on returned occurrences. */
  limit?: number;
}

/**
 * Occurrence start times of a series whose start falls within [from, to].
 * A non-recurring series yields its single start if it is in range.
 */
export function expandOccurrences(seriesStart: Date, rule: Recurrence | null | undefined, from: Date, to: Date, options: ExpandOptions = {}): Date[] {
  const limit = options.limit ?? 1000;
  const excluded = new Set(options.exdates ?? []);
  if (!rule) {
    const inRange = seriesStart >= from && seriesStart <= to && !excluded.has(formatLocalDate(seriesStart));
    return inRange ? [seriesStart] : [];
  }
  const out: Date[] = [];
  let produced = 0;
  let guard = 0;
  for (const occ of generate(seriesStart, rule, periodsBefore(seriesStart, rule, from))) {
    if (++guard > 200_000) break;
    if (occ > to) break;
    if (rule.until && formatLocalDate(occ) > rule.until) break;
    produced++;
    if (rule.count !== undefined && produced > rule.count) break;
    if (occ >= from && !excluded.has(formatLocalDate(occ))) {
      out.push(occ);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/** Next occurrence at or after `from`, or null when the series has ended. */
export function nextOccurrence(seriesStart: Date, rule: Recurrence | null | undefined, from: Date, exdates?: readonly string[]): Date | null {
  const horizon = new Date(from.getFullYear() + 10, from.getMonth(), from.getDate());
  const found = expandOccurrences(seriesStart, rule, from, horizon, { exdates, limit: 1 });
  return found[0] ?? null;
}
